import React, { useState, useEffect, useCallback } from 'react';

const C = {
  forest: '#2e7d32', forestDark: '#1b5e20', forestLight: '#c8e6c9',
  forestPale: '#e8f5e9', brownie: '#8b4513', brownieLt: '#a0673e',
  cream: '#faf6f1', creamDk: '#f0ebe3', charcoal: '#36342f',
  charcoalLt: '#5c5a54', sage: '#87a96b', terra: '#c14242', white: '#fff',
};

const GedcomImport = ({ onTreeLoaded, onImportComplete }) => {
  const [trees, setTrees] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [treeName, setTreeName] = useState('');
  const [showImport, setShowImport] = useState(false);

  const fetchTrees = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/gedcom/trees', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTrees(data);
      }
    } catch (err) {
      console.error('Error fetching trees:', err);
    }
  }, []);

  useEffect(() => { fetchTrees(); }, [fetchTrees]);

  const scrollToChat = () => {
    setTimeout(() => {
      const chatArea = document.querySelector('.chat-messages, .chat-input-area, .chat-main');
      if (chatArea) {
        chatArea.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }
    }, 300);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'ged' && ext !== 'gedcom') {
      setError('Please upload a .ged or .gedcom file');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('gedcomFile', file);
      formData.append('treeName', treeName || file.name.replace(/\.(ged|gedcom)$/i, ''));

      const res = await fetch('/api/gedcom/upload', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await res.json();
      if (data.success) {
        setUploadResult(data);
        setShowImport(false);
        setTreeName('');
        fetchTrees();
        if (onTreeLoaded) onTreeLoaded(data);
        scrollToChat();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const loadTree = async (treeId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/gedcom/tree/${treeId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (onTreeLoaded) onTreeLoaded(data);
        scrollToChat();
      }
    } catch (err) {
      setError('Failed to load tree');
    }
  };

  const deleteTree = async (treeId) => {
    if (!window.confirm('Delete this family tree? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/gedcom/tree/${treeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      fetchTrees();
    } catch (err) {
      setError('Failed to delete tree');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  return (
    <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.creamDk}`, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px', background: C.cream,
        borderBottom: `1px solid ${C.creamDk}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>🌳</span>
          <span style={{ fontWeight: 600, color: C.charcoal, fontSize: 15 }}>Family Trees</span>
          {trees.length > 0 && (
            <span style={{
              background: C.forestPale, color: C.forestDark, fontSize: 11,
              padding: '2px 8px', borderRadius: 10, fontWeight: 600,
            }}>{trees.length}</span>
          )}
        </div>
        <button
          onClick={() => setShowImport(!showImport)}
          style={{
            background: C.forest, color: C.white, border: 'none',
            padding: '8px 16px', borderRadius: 6, fontSize: 13,
            fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s',
          }}
          onMouseEnter={e => e.target.style.background = C.forestDark}
          onMouseLeave={e => e.target.style.background = C.forest}
        >
          + Import GEDCOM
        </button>
      </div>

      {/* Import Area */}
      {showImport && (
        <div style={{ padding: 20, borderBottom: `1px solid ${C.creamDk}` }}>
          <input
            type="text"
            placeholder="Tree name (optional)"
            value={treeName}
            onChange={e => setTreeName(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 8,
              border: `1px solid ${C.creamDk}`, fontSize: 14,
              marginBottom: 12, outline: 'none', fontFamily: 'inherit',
            }}
          />
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${dragOver ? C.forest : C.creamDk}`,
              borderRadius: 12, padding: 32, textAlign: 'center',
              background: dragOver ? C.forestPale : C.cream,
              transition: 'all 0.2s', cursor: 'pointer',
            }}
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.ged,.gedcom';
              input.onchange = e => handleUpload(e.target.files[0]);
              input.click();
            }}
          >
            {uploading ? (
              <div>
                <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
                <p style={{ color: C.charcoalLt, fontSize: 14 }}>Parsing your family tree...</p>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
                <p style={{ color: C.charcoal, fontSize: 14, fontWeight: 500, margin: '0 0 4px' }}>
                  Drop a .ged file here or click to browse
                </p>
                <p style={{ color: C.charcoalLt, fontSize: 12, margin: 0 }}>
                  Export from Ancestry, FamilySearch, MyHeritage, or any genealogy software
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 20px', background: 'rgba(193,66,66,0.06)', color: C.terra, fontSize: 13, borderBottom: `1px solid ${C.creamDk}` }}>
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', color: C.terra, cursor: 'pointer', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Upload Result */}
      {uploadResult && (
        <div style={{ padding: 20, background: C.forestPale, borderBottom: `1px solid ${C.creamDk}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <span style={{ fontWeight: 600, color: C.forestDark, fontSize: 14 }}>
              {uploadResult.name} imported — Gene is ready to research your tree
            </span>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: C.charcoalLt }}>
            <span><strong style={{ color: C.forest }}>{uploadResult.totalIndividuals}</strong> people</span>
            <span><strong style={{ color: C.forest }}>{uploadResult.totalFamilies}</strong> families</span>
            <span>{uploadResult.surnames.join(', ')}</span>
            {uploadResult.dateRange.earliest && (
              <span>{uploadResult.dateRange.earliest}–{uploadResult.dateRange.latest}</span>
            )}
          </div>
          <button onClick={() => setUploadResult(null)} style={{
            marginTop: 10, background: 'none', border: 'none',
            color: C.forestDark, cursor: 'pointer', fontSize: 12, textDecoration: 'underline',
          }}>Dismiss</button>
        </div>
      )}

      {/* Tree List */}
      {trees.length > 0 && (
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {trees.map(tree => (
            <div
              key={tree._id}
              style={{
                padding: '12px 20px',
                borderBottom: `1px solid ${C.creamDk}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: C.white, cursor: 'pointer', transition: 'background 0.15s',
              }}
              onClick={() => loadTree(tree._id)}
              onMouseEnter={e => e.currentTarget.style.background = C.cream}
              onMouseLeave={e => e.currentTarget.style.background = C.white}
            >
              <div>
                <div style={{ fontWeight: 500, color: C.charcoal, fontSize: 14 }}>
                  🌳 {tree.name}
                </div>
                <div style={{ fontSize: 12, color: C.charcoalLt, marginTop: 2 }}>
                  {tree.totalIndividuals} people · {tree.surnames.slice(0, 3).join(', ')}
                  {tree.surnames.length > 3 ? ` +${tree.surnames.length - 3} more` : ''}
                  {tree.dateRange.earliest && ` · ${tree.dateRange.earliest}–${tree.dateRange.latest}`}
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteTree(tree._id); }}
                style={{
                  background: 'none', border: 'none', color: C.charcoalLt,
                  cursor: 'pointer', fontSize: 16, padding: 4, opacity: 0.5,
                }}
                onMouseEnter={e => { e.target.style.opacity = '1'; e.target.style.color = C.terra; }}
                onMouseLeave={e => { e.target.style.opacity = '0.5'; e.target.style.color = C.charcoalLt; }}
                title="Delete tree"
              >✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {trees.length === 0 && !showImport && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🌳</div>
          <p style={{ color: C.charcoal, fontSize: 14, fontWeight: 500, marginBottom: 4 }}>No family trees yet</p>
          <p style={{ color: C.charcoalLt, fontSize: 13, marginBottom: 16 }}>
            Import a GEDCOM file from Ancestry, FamilySearch, or MyHeritage to get started
          </p>
          <button
            onClick={() => setShowImport(true)}
            style={{
              background: C.forest, color: C.white, border: 'none',
              padding: '10px 24px', borderRadius: 6, fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
            }}
          >Import Your First Tree</button>
        </div>
      )}
    </div>
  );
};

export default GedcomImport;
